// The single-workspace view: a full-height rail on the left, a content pane on
// the right. Mounted on workspaces#show by the page module.
//
// This file owns the frame — the rail, the ContentView switch, and the
// workspace-level state. Each pane lives in workspace/:
//
//   quick_start.tsx      — the launch form (objective, agent count, mode)
//   markdown_file.tsx    — a .md document: rendered, click to edit raw
//   agents.tsx           — the workspace's agents, from the database
//   findings.tsx         — shared/public_findings in the sandbox: the results
//   literature.tsx       — shared/literature: the papers they have read
//   local_instructions.tsx — how to start a local runtime (development only)
//
import { createRoot } from "react-dom/client"
import { type JSX, useEffect, useRef, useState } from "react"
import { type AgentRecord, fetchAgents, fetchObjective, saveWorkspace } from "./workspace/api"
import { AgentChat } from "./workspace/agent_chat"
import { Agents } from "./workspace/agents"
import { MarkdownFile } from "./workspace/markdown_file"
import { Findings } from "./workspace/findings"
import { Literature } from "./workspace/literature"
import { LocalInstructions } from "./workspace/local_instructions"
import { QuickStart } from "./workspace/quick_start"
import { type ContentView } from "./workspace/types"
import { WorkspaceName } from "./workspace/workspace_name"
import { WorkspaceMenu } from "./workspace/workspace_menu"

// A workspace created from a description lands on this page before the job
// that expands it into objective.md has finished, so we poll for the objective
// instead of leaving the pane empty until a reload. Generation is one model
// call, so the tick is short and the wait is bounded — a job that has given up
// would otherwise leave the page polling forever.
const OBJECTIVE_POLL_INTERVAL_MS = 2000
const OBJECTIVE_POLL_TIMEOUT_MS = 3 * 60 * 1000

type WorkspaceProps = {
  name: string
  // The one-line "what is this workspace for". Input to objective generation.
  description: string
  // The full objective.md. Generated from the description, then hand-edited.
  objective: string
  // Whether the objective was still on its way when the page was rendered.
  // Purely the poll gate — nothing in the UI says a generation is running.
  awaitingObjective: boolean
  objectiveUrl: string
  updateUrl: string
  agentsUrl: string
  agentModels: [string, string][]
  quickstartUrl: string
  quickstarted: boolean
  // Whether this workspace's runtime is a process on the developer's machine
  // rather than an E2B sandbox. Development-only, and the reason the rail
  // grows an instructions pane.
  local: boolean
  workspaceDbId: string
  runtimeUrl: string
  runtimeDir: string
  filesUrl: string
  fileUrl: string
  renameUrl: string
  // The ⋯ menu beside the name in the rail. Sharing and deleting are the
  // owner's alone, so a non-owner gets no URLs and the menu renders nothing.
  canManage: boolean
  sharingUrl: string
  deleteUrl: string
}

type RailEntry = {
  id: string
  label: string
  view: ContentView
}

// Quick start is a one-time flow: once the workspace has been quickstarted
// the entry disappears and the app opens on the objective instead. Local
// instructions appear only for a workspace whose runtime is a process on this
// machine, which is a development-only situation.
function railEntries(quickstarted: boolean, local: boolean): RailEntry[] {
  const entries: RailEntry[] = [
    { id: "agents", label: "Agents", view: { kind: "agents" } },
    { id: "objective", label: "Objective", view: { kind: "objective" } },
    { id: "findings", label: "Findings", view: { kind: "findings" } },
    { id: "literature", label: "Literature", view: { kind: "literature" } },
  ]
  if (!quickstarted) entries.unshift({ id: "quick-start", label: "Quick start", view: { kind: "quick-start" } })
  if (local) entries.push({ id: "local", label: "Local instructions", view: { kind: "local-instructions" } })
  return entries
}

function WorkspaceApp({ name, objective: initialObjective, awaitingObjective: initialAwaitingObjective, objectiveUrl, updateUrl, agentsUrl, agentModels, quickstartUrl, quickstarted: initialQuickstarted, local, workspaceDbId, runtimeUrl, runtimeDir, filesUrl, fileUrl, renameUrl, canManage, sharingUrl, deleteUrl }: WorkspaceProps): JSX.Element {
  const [quickstarted, setQuickstarted] = useState(initialQuickstarted)
  // Landing view: quick start when it's still on offer, otherwise the Agents
  // section. Once the roster loads, a workspace with agents jumps to its
  // first agent (see the fetch below) — unless the user has already
  // navigated somewhere themselves.
  const [view, setView] = useState<ContentView>(initialQuickstarted ? { kind: "agents" } : { kind: "quick-start" })
  const [objective, setObjective] = useState(initialObjective)
  const [awaitingObjective, setAwaitingObjective] = useState(initialAwaitingObjective)
  const [workspaceName, setWorkspaceName] = useState(name)
  const userNavigatedRef = useRef(false)

  function navigate(next: ContentView): void {
    userNavigatedRef.current = true
    setView(next)
  }

  // Both the arrival of the generated objective and the owner writing one by
  // hand come through here, and either ends the wait: the job stands down
  // once the column is no longer blank.
  function applyObjective(next: string): void {
    setObjective(next)
    setAwaitingObjective(false)
  }

  // Poll until the generation job has written objective.md, then stop. Nothing
  // to arm when the workspace already has an objective (or has no description
  // to make one from), which is the common case.
  useEffect(() => {
    if (!awaitingObjective || !objectiveUrl) return

    let cancelled = false
    const startedAt = Date.now()

    async function pull(): Promise<void> {
      const state = await fetchObjective(objectiveUrl)
      if (cancelled) return

      if (state && !state.generating) {
        applyObjective(state.objective)
      } else if (Date.now() - startedAt >= OBJECTIVE_POLL_TIMEOUT_MS) {
        // The job has run out of retries or is wedged. Give up quietly and
        // leave the pane writable — an empty objective is a usable workspace.
        setAwaitingObjective(false)
      }
    }

    const timer = setInterval(() => void pull(), OBJECTIVE_POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [awaitingObjective, objectiveUrl])

  // The agent roster lives here (not in the Agents pane) because the rail
  // shows it too: the Agents entry always carries a sublist of the
  // workspace's agents, styled like the Organization section of the
  // settings sidebar.
  const [agents, setAgents] = useState<AgentRecord[] | null>(null)
  const [agentsFailed, setAgentsFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    void fetchAgents(agentsUrl).then((loaded) => {
      if (cancelled) return
      if (loaded) {
        setAgents(loaded)
        // A workspace with agents opens on its first agent. (Quick start
        // never shows alongside agents, so this can't steal its spot.)
        if (!userNavigatedRef.current && loaded.length > 0) {
          setView({ kind: "agent", agentId: loaded[0].id })
        }
      } else {
        setAgentsFailed(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [agentsUrl])

  // The moment the roster shows any agent, quick start stops being offered.
  useEffect(() => {
    if (agents && agents.length > 0) setQuickstarted(true)
  }, [agents])

  return (
    <div className="workspace">
      <nav className="workspace__rail" aria-label="Workspace sections">
        <div className="workspace__rail-head">
          <WorkspaceName name={workspaceName} updateUrl={updateUrl} onChange={setWorkspaceName} />
          <WorkspaceMenu
            workspaceName={workspaceName}
            sharingUrl={sharingUrl}
            deleteUrl={deleteUrl}
            canManage={canManage}
          />
        </div>

        <ul className="workspace__rail-list">
          {railEntries(quickstarted, local).map((entry) => (
            <li key={entry.id}>
              <button
                type="button"
                className={`workspace__rail-item${isActive(entry, view) ? " is-active" : ""}`}
                aria-current={isActive(entry, view) ? "true" : undefined}
                onClick={() => navigate(entry.view)}
              >
                {entry.label}
              </button>
              {entry.id === "agents" && agents && agents.length > 0 && (
                <ul className="workspace__rail-sublist">
                  {agents.map((agent) => (
                    <li key={agent.id}>
                      <button
                        type="button"
                        className={`workspace__rail-subitem${view.kind === "agent" && view.agentId === agent.id ? " is-active" : ""}`}
                        aria-current={view.kind === "agent" && view.agentId === agent.id ? "true" : undefined}
                        onClick={() => navigate({ kind: "agent", agentId: agent.id })}
                      >
                        {agent.name ?? "Unnamed agent"}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>

      <main className="workspace__content">
        <Content
          view={view}
          objective={objective}
          workspaceDbId={workspaceDbId}
          runtimeUrl={runtimeUrl}
          runtimeDir={runtimeDir}
          filesUrl={filesUrl}
          fileUrl={fileUrl}
          renameUrl={renameUrl}
          updateUrl={updateUrl}
          agentsUrl={agentsUrl}
          agentModels={agentModels}
          quickstartUrl={quickstartUrl}
          agents={agents}
          agentsFailed={agentsFailed}
          onAgentCreated={(agent) => setAgents((current) => [ ...(current ?? []), agent ])}
          onAgentDeleted={(agentId) => {
            setAgents((current) => current?.filter((a) => a.id !== agentId) ?? current)
            // Leaving the chat of an agent that no longer exists open would
            // only offer controls that now fail.
            setView((current) =>
              current.kind === "agent" && current.agentId === agentId ? { kind: "agents" } : current)
          }}
          onAgentChanged={(updated) =>
            setAgents((current) => current?.map((a) => (a.id === updated.id ? updated : a)) ?? current)}
          onObjectiveChange={applyObjective}
          onOpen={navigate}
          onQuickstarted={() => {
            setQuickstarted(true)
            navigate({ kind: "agents" })
          }}
        />
      </main>
    </div>
  )
}

// A rail entry stays lit while you're inside what it opened — a findings file
// keeps "Findings" active.
function isActive(entry: RailEntry, view: ContentView): boolean {
  if (entry.view.kind === view.kind) return true
  return entry.id === "agents" && view.kind === "agent"
}

type ContentProps = {
  view: ContentView
  objective: string
  workspaceDbId: string
  runtimeUrl: string
  runtimeDir: string
  filesUrl: string
  fileUrl: string
  renameUrl: string
  updateUrl: string
  agentsUrl: string
  agentModels: [string, string][]
  quickstartUrl: string
  agents: AgentRecord[] | null
  agentsFailed: boolean
  onAgentCreated: (agent: AgentRecord) => void
  onAgentDeleted: (agentId: string) => void
  onAgentChanged: (agent: AgentRecord) => void
  onObjectiveChange: (value: string) => void
  onOpen: (view: ContentView) => void
  onQuickstarted: () => void
}

function Content({ view, objective, workspaceDbId, runtimeUrl, runtimeDir, filesUrl, fileUrl, renameUrl, updateUrl, agentsUrl, agentModels, quickstartUrl, agents, agentsFailed, onAgentCreated, onAgentDeleted, onAgentChanged, onObjectiveChange, onOpen, onQuickstarted }: ContentProps): JSX.Element {
  switch (view.kind) {
    case "quick-start":
      return <QuickStart objective={objective} quickstartUrl={quickstartUrl} onOpen={onOpen} onQuickstarted={onQuickstarted} />

    case "objective":
      return (
        <MarkdownFile
          key="objective"
          name="objective.md"
          title="Objective"
          meta="The overall objective of the workspace."
          body={objective}
          onSave={(next) => saveWorkspace(updateUrl, { objective: next }).then((ok) => {
            if (ok) onObjectiveChange(next)
            return ok
          })}
        />
      )

    case "agents":
      return (
        <Agents
          agents={agents}
          failed={agentsFailed}
          agentsUrl={agentsUrl}
          modelOptions={agentModels}
          onAgentCreated={onAgentCreated}
          onAgentDeleted={onAgentDeleted}
          onOpenAgent={(agentId) => onOpen({ kind: "agent", agentId })}
        />
      )

    case "agent":
      return (
        <AgentChat
          key={view.agentId}
          agentsUrl={agentsUrl}
          agentId={view.agentId}
          filesUrl={filesUrl}
          fileUrl={fileUrl}
          onAgentChanged={onAgentChanged}
          onBack={() => onOpen({ kind: "agents" })}
        />
      )

    case "literature":
      return (
        <Literature
          filesUrl={filesUrl}
          fileUrl={fileUrl}
          openPath={view.paperPath}
          onOpenPaper={(paperPath) => onOpen({ kind: "literature", paperPath })}
        />
      )

    case "local-instructions":
      return <LocalInstructions workspaceDbId={workspaceDbId} runtimeUrl={runtimeUrl} runtimeDir={runtimeDir} />

    case "findings":
      return (
        <Findings
          filesUrl={filesUrl}
          fileUrl={fileUrl}
          renameUrl={renameUrl}
          openPath={view.path}
          onOpenFinding={(path) => onOpen({ kind: "findings", path })}
        />
      )
  }
}

function parseModels(raw: string | undefined): [string, string][] {
  try {
    const parsed = JSON.parse(raw ?? "[]") as unknown
    if (Array.isArray(parsed)) return parsed as [string, string][]
  } catch {
    // fall through
  }
  return []
}

export function mountWorkspaceApp(): void {
  const el = document.querySelector<HTMLElement>("[data-workspace-app]")
  if (!el) return

  createRoot(el).render(
    <WorkspaceApp
      name={el.dataset.workspaceName ?? ""}
      description={el.dataset.workspaceDescription ?? ""}
      objective={el.dataset.workspaceObjective ?? ""}
      awaitingObjective={el.dataset.workspaceAwaitingObjective === "true"}
      objectiveUrl={el.dataset.workspaceObjectiveUrl ?? ""}
      updateUrl={el.dataset.workspaceUpdateUrl ?? ""}
      agentsUrl={el.dataset.workspaceAgentsUrl ?? ""}
      agentModels={parseModels(el.dataset.workspaceAgentModels)}
      quickstartUrl={el.dataset.workspaceQuickstartUrl ?? ""}
      quickstarted={el.dataset.workspaceQuickstarted === "true"}
      local={el.dataset.workspaceLocal === "true"}
      workspaceDbId={el.dataset.workspaceDbId ?? ""}
      runtimeUrl={el.dataset.workspaceRuntimeUrl ?? ""}
      runtimeDir={el.dataset.workspaceRuntimeDir ?? ""}
      filesUrl={el.dataset.workspaceFilesUrl ?? ""}
      fileUrl={el.dataset.workspaceFileUrl ?? ""}
      renameUrl={el.dataset.workspaceRenameUrl ?? ""}
      canManage={el.dataset.workspaceCanManage === "true"}
      sharingUrl={el.dataset.workspaceSharingUrl ?? ""}
      deleteUrl={el.dataset.workspaceDeleteUrl ?? ""}
    />
  )
}
